import { useAgentContext, CopilotChat, useCopilotKit, useAgent } from '@copilotkit/react-core/v2'
import { useApolloClient } from '@graphcommerce/graphql'
import { CustomerTokenDocument } from '@graphcommerce/magento-customer'
import { Tooltip } from '@mui/material'
import { useMemo, useEffect, useCallback } from 'react'
import { useAgentConnection } from '../../hooks/useAgentConnection'
import { useAiChatSession } from '../../hooks/useAiChatSession'
import { useHumanMode } from '../../hooks/useHumanMode'
import { useShowEmailFormAction } from '../../hooks/useShowEmailFormAction'
import { useShowRushDeliveryFormAction } from '../../hooks/useShowRushDeliveryFormAction'
import { useShowSignupFormAction } from '../../hooks/useShowSignupFormAction'
import { AiChatConnectingOverlay } from './AiChatConnectingOverlay'
import { AiChatContext, useAiChatContext } from './AiChatContext'
import { AiChatHeader } from './AiChatHeader'
import { FilteredMessageView } from './FilteredMessageView'

import { PricePointsElement } from './PricePointsElement'
import { ProductsCarouselElement } from './ProductsCarouselElement'
import type { Product } from './ProductsCarouselElement'

type RawProduct = Record<string, unknown>

function urlKeyFromUrl(url: string): string {
  return url.replace(/\/$/, '').split('/').pop() ?? ''
}

function ProductTextListRenderer({
  status,
  args,
}: {
  status?: string
  args: Record<string, unknown>
}) {
  const { copilotkit } = useCopilotKit()
  const { agent } = useAgent()
  const raw = (args?.products as RawProduct[] | undefined) ?? []
  if ((status === 'inProgress' && agent.isRunning) || !raw.length) return null
  return (
    <div className='my-2 flex flex-col gap-1 rounded-lg border border-light-dark-gray bg-lightest-gray/50 p-3'>
      {raw.map((p, i) => {
        const name = String(p.name || p.title || '')
        const urlKey = urlKeyFromUrl(String(p.url || '')) || String(p.id || '')
        return (
          <button
            key={urlKey || i}
            type='button'
            onClick={() => {
              agent.addMessage({
                id: crypto.randomUUID(),
                role: 'user',
                content: `Get templates for ${name} (url_key: ${urlKey})`,
              })
              copilotkit.runAgent({ agent }).catch(() => {})
            }}
            className='rounded px-3 py-2 text-left text-base hover:bg-lighter-templi-purple hover:text-bright-templi-purple'
          >
            {name}
          </button>
        )
      })}
    </div>
  )
}

function TemplateListRenderer({
  status,
  args,
}: {
  status?: string
  args: Record<string, unknown>
}) {
  const { agent } = useAgent()
  const templates = (args?.templates as Array<{ label?: string; url?: string }> | undefined) ?? []
  if (status === 'inProgress' && agent.isRunning) return null
  if (!templates.length) return null
  return (
    <div className='my-2 inline-flex flex-col gap-2'>
      {templates.map((t, i) => (
        <a
          key={t.url || i}
          href={t.url ?? '#'}
          target='_blank'
          rel='noopener noreferrer'
          className='flex items-center gap-2 rounded border border-light-dark-gray px-3 py-2 text-[12px] hover:border-bright-templi-purple hover:text-bright-templi-purple'
        >
          <span>↓</span>
          <span>{t.label || 'Template'}</span>
        </a>
      ))}
    </div>
  )
}

function ProductCarouselRenderer({ status, args }: { status?: string; args: Record<string, unknown> }) {
  const { mode } = useAiChatContext()
  const { copilotkit } = useCopilotKit()
  const { agent } = useAgent()
  if (status === 'inProgress' && agent.isRunning) return null
  // Handle both: args.products = [...] (LLM passes list directly)
  // and: args.products = {products: [...]} (LLM passes the get_products return value directly)
  const rawValue = args?.products
  const raw: Record<string, unknown>[] = Array.isArray(rawValue)
    ? rawValue
    : Array.isArray((rawValue as any)?.products)
      ? (rawValue as any).products
      : []
  const products: Product[] = raw.map((p) => ({
    name: String(p.name || ''),
    url: String(p.url || ''),
    image_url: String(p.image_url || ''),
    moq: String(p.moq || ''),
    production_time: String(p.production_time || ''),
    free_shipping: String(p.free_shipping || ''),
  }))
  if (!products.length) return null
  const onProductSelect = (name: string, url: string) => {
    const slug = urlKeyFromUrl(url)
    agent.addMessage({
      id: crypto.randomUUID(),
      role: 'user',
      content: `Pull up ${name} (url_key: ${slug})`,
    })
    copilotkit.runAgent({ agent }).catch(() => {})
  }
  return (
    <ProductsCarouselElement
      products={products}
      columns={mode === 'chat' ? 4 : 2}
      onProductSelect={onProductSelect}
    />
  )
}

function PricePointsRenderer({ status, args }: { status?: string; args: Record<string, unknown> }) {
  const { mode } = useAiChatContext()
  const { agent } = useAgent()
  const name = String(args?.name || args?.product_name || '')
  const image_url = String(args?.image_url || '')
  const url = args?.url ? String(args.url) : undefined
  const production_time = args?.production_time ? String(args.production_time) : undefined
  const raw = (args?.price_points as Array<Record<string, unknown>> | undefined) ?? []
  const price_points = raw.map((p) => ({
    quantity: (p.quantity ?? p.qty ?? '') as number | string,
    price: (p.price ?? p.unit_price ?? 0) as number | string,
    unit: p.unit ? String(p.unit) : undefined,
  }))
  const sku = args?.sku ? String(args.sku) : undefined
  const configurable_options = Array.isArray(args?.configurable_options)
    ? (args.configurable_options as import('./VariantSelector').ConfigurableOption[])
    : undefined
  const variants = Array.isArray(args?.variants)
    ? (args.variants as import('./VariantSelector').Variant[])
    : undefined
  // matches the PDP logic: !selectedVariantDesignId && product.requires_artwork
  // (no design ID concept in chat, so we only check requires_artwork)
  const artwork_required = Boolean(args?.requires_artwork ?? args?.artwork_required)
  if (!name && !image_url && !price_points.length) return null
  // If the agent run has ended but CopilotKit still shows inProgress (the tool-result
  // message was dropped — e.g. by a browser extension interrupting the SSE stream),
  // render with whatever args arrived in the tool-call message.
  if (status === 'inProgress' && agent.isRunning) return null
  return (
    <PricePointsElement
      name={name}
      image_url={image_url || undefined}
      url={url}
      production_time={production_time}
      price_points={price_points}
      compact={mode === 'chat'}
      sku={sku}
      configurable_options={configurable_options}
      variants={variants}
      artwork_required={artwork_required}
    />
  )
}

type ChatMode = 'popup' | 'sidebar' | 'chat'

interface AiChatFabProps {
  className?: string
}

// Container classes per mode. The CopilotChat inside never remounts — only
// the wrapper's className changes, so chat history and state are preserved.
const CONTAINER: Record<ChatMode, string> = {
  popup:
    'fixed top-16 right-4 z-50 flex flex-col w-[400px] h-[600px] bg-pure-white shadow-xl rounded-lg border border-light-dark-gray overflow-hidden',
  sidebar:
    'fixed top-0 right-0 z-50 flex flex-col w-[500px] h-full bg-pure-white shadow-xl border-l border-light-dark-gray',
  chat: 'fixed inset-0 z-50 flex flex-col bg-pure-white',
}

export function AiChatFab({ className }: AiChatFabProps) {
  const apolloClient = useApolloClient()
  const token =
    apolloClient.cache.readQuery({ query: CustomerTokenDocument })?.customerToken?.token ?? ''

  useAgentContext({ description: 'mg_user_front_token', value: token })

  const { isConnecting, resetConnection } = useAgentConnection()
  const { open, setOpen, mode, switchMode, handleClose, handleReset } =
    useAiChatSession(resetConnection)

  useShowEmailFormAction()
  useShowSignupFormAction()
  useShowRushDeliveryFormAction()
  const { showContactForm, setShowContactForm, humanModeActive, humanModeStartedAt } = useHumanMode()

  const handleResetAndClearForm = useCallback(() => {
    setShowContactForm(false)
    handleReset()
  }, [handleReset, setShowContactForm])

  const { copilotkit } = useCopilotKit()
  useEffect(() => {
    // Renderer-only — the backend has displayProductCarousel as a LangChain tool,
    // so the tool call is resolved by the backend. No frontend handler needed.
    const core = copilotkit as { addHookRenderToolCall: (r: object) => void }
    core.addHookRenderToolCall({
      name: 'displayProductCarousel',
      args: null,
      render: ProductCarouselRenderer,
    })
    core.addHookRenderToolCall({
      name: 'showProductTextList',
      args: null,
      render: ProductTextListRenderer,
    })
    core.addHookRenderToolCall({
      name: 'showTemplateList',
      args: null,
      render: TemplateListRenderer,
    })
    core.addHookRenderToolCall({
      name: 'showPricePoints',
      args: null,
      render: PricePointsRenderer,
    })
  }, [copilotkit])

  const chatLabels = useMemo(
    () => ({ modalHeaderTitle: 'Chat', welcomeMessageText: 'Hi! How can I help you today?' }),
    [],
  )

  const chatContextValue = useMemo(
    () => ({ mode, onClose: handleClose, showContactForm, setShowContactForm, humanModeActive, humanModeStartedAt }),
    [mode, handleClose, showContactForm, setShowContactForm, humanModeActive, humanModeStartedAt],
  )

  return (
    <div className={className}>
      {/* ── FAB button ── */}
      <Tooltip
        title="Allison is Templi's AI assistant."
        PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] }}
      >
        <button
          type='button'
          onClick={() => setOpen((v) => !v)}
          className={`flex w-auto items-center justify-center rounded-52xl border px-2 py-1.5 text-center hover:shadow-md text-pure-white
            ${
              open
                ? 'bg-bright-templi-purple-active'
                : 'bg-bright-templi-purple hover:bg-bright-templi-purple-hover'
            } active:bg-bright-templi-purple-active`}
        >
          <span className='Type-XL-Bold truncate whitespace-nowrap px-2 md:px-5'>Chat</span>
        </button>
      </Tooltip>

      {/* Single CopilotChat instance — stays mounted while open so history
          and backend thread state survive mode switches. Only the wrapper
          div's className changes when the user switches popup/sidebar/chat. */}
      {open && (
        <AiChatContext.Provider value={chatContextValue}>
          <div className={CONTAINER[mode]}>
            {isConnecting && <AiChatConnectingOverlay />}

            <AiChatHeader
              mode={mode}
              onClose={handleClose}
              onSwitchMode={switchMode}
              onReset={handleResetAndClearForm}
            />

            <div
              className='flex-1 overflow-hidden'
              data-connecting={isConnecting ? 'true' : undefined}
            >
              <CopilotChat labels={chatLabels} messageView={FilteredMessageView} />
            </div>
          </div>
        </AiChatContext.Provider>
      )}
    </div>
  )
}
